/**
 * Testimonials: keep the drift, but let people take the wheel.
 *
 * The row used to be a pure CSS marquee — a track with `width: max-content`
 * moved by `transform: translateX(...)`. Because the movement was a transform,
 * the viewport had nothing to scroll: you could watch the cards go by but never
 * reach for one, and the only way to read a card that had just passed was to
 * wait a full lap. Manual scrolling existed solely behind
 * `prefers-reduced-motion`.
 *
 * The transform is gone. The viewport is a real horizontal scroller now, and the
 * drift is the same scroller being nudged along a fraction of a pixel per frame.
 * Auto and manual are therefore the same mechanism and cannot fight each other:
 * a swipe, a trackpad flick, a drag of the scrollbar or a keyboard tab all just
 * move the thing that was already moving.
 *
 * The track still renders more than one copy of the cards. Once the scroll
 * passes the width of a single copy the offset is reduced by exactly that width,
 * which lands on an identical frame — so the loop stays endless in both
 * directions without a jump.
 *
 * It holds still when a person is busy with it (pointer down, wheel, hover,
 * keyboard focus) and for a few seconds after, when the section is off screen,
 * and entirely when the visitor asks for reduced motion.
 */
(function () {
  'use strict';

  const RESUME_AFTER_MS = 2500;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setup(viewport) {
    const track = viewport.querySelector('.brand-reviews__marquee');
    if (!track || viewport.dataset.sbScroller === 'on') return;
    viewport.dataset.sbScroller = 'on';

    const copies = Math.max(1, parseInt(track.style.getPropertyValue('--brand-reviews-copies'), 10) || 2);

    // The old animation crossed one copy in `animation-duration`. Deriving the
    // speed from the same numbers keeps the pace the shop already had.
    const seconds = parseFloat(track.style.animationDuration) || 90;

    let paused = 0; // timestamp until which the drift stays parked
    let visible = true;
    let last = null;

    const copyWidth = () => track.scrollWidth / copies;
    const pause = () => { paused = performance.now() + RESUME_AFTER_MS; };

    for (const type of ['pointerdown', 'touchstart', 'wheel', 'mouseenter', 'focusin']) {
      viewport.addEventListener(type, pause, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => { for (const entry of entries) visible = entry.isIntersecting; },
        { threshold: 0 }
      ).observe(viewport);
    }

    function frame(now) {
      const delta = last === null ? 0 : now - last;
      last = now;

      const width = copyWidth();

      if (width > 0) {
        // Keep the offset inside one copy whichever way it was moved, so a
        // person can scroll backwards past the start and still loop.
        if (viewport.scrollLeft >= width) viewport.scrollLeft -= width;
        else if (viewport.scrollLeft <= 0) viewport.scrollLeft += width;

        const idle = now >= paused;
        if (idle && visible && !reduceMotion.matches && delta > 0 && delta < 200) {
          viewport.scrollLeft += (width / (seconds * 1000)) * delta;
        }
      }

      window.requestAnimationFrame(frame);
    }

    window.requestAnimationFrame(frame);
  }

  function init() {
    document.querySelectorAll('.brand-reviews__viewport').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
